import { throwError, Observable, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { AuthService, User } from './auth.service';
import { OverlayService } from './overlay.service';
import { SmartLocalStorageService } from './smart-local-storage.service';

declare const $;

@Injectable()
export class CreatorAuthenticationInterceptor implements HttpInterceptor {
	constructor(
		private auth: AuthService,
		private router: Router,
		private storage: SmartLocalStorageService,
		private overlay: OverlayService
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> | Observable<any> {
		const re = new RegExp(/(email=)\S+@\S+.\S+(&password=)/, 'g');

		if (req.url.endsWith('login') && typeof req.body === 'string' && re.test(req.body) && req.method === 'POST') {
			return next.handle(this.setLoginMethodHeader(req)).pipe(
				catchError((err) => {
					if (err.status === 401) {
						// auto logout if 401 response returned from api
						this.auth.logout();
						location.reload();
					}

					const error = err.error.message || err.statusText;
					return throwError(error);
				})
			);
		}

		const user: User = this.auth.getUser();
		const isUser = Boolean(this.storage.isUser(user));
		const isExpired = Boolean(this.storage.isExpired());

		if (isExpired || !isUser) {
			this.router.navigate(['/login']);
			this.overlay.updateOverlay(false);
			return EMPTY;
		}

		if (req.method === 'POST' && (req.url.endsWith('uploadFile') || req.url.endsWith('cms'))) {
			return next.handle(this.setUploadMethodHeader(req, user)).pipe(
				catchError((err) => {
					if (err.status === 401) {
						// auto logout if 401 response returned from api
						this.auth.logout();
						location.reload();
					}

					const error = err.error.message || err.statusText;
					return throwError(error);
				})
			);
		} else {
			return next.handle(this.setAuthorizationHeader(req, user)).pipe(
				catchError((err) => {
					if (err.status === 401) {
						// auto logout if 401 response returned from api
						this.auth.logout();
						location.reload();
					}

					const error = err.error.message || err.statusText;
					return throwError(error);
				})
			);
		}
	}

	private setUploadMethodHeader(req: HttpRequest<any>, user: User): HttpRequest<any> {
		const body = Object.assign({}, req.body);

		const formData: FormData = new FormData();
		formData.append('file', body.file, body.file.name);

		return req.clone({
			body: formData
		});
	}

	private setLoginMethodHeader(req: HttpRequest<any>): HttpRequest<any> {
		return req.clone({
			setHeaders: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
		});
	}

	private setAuthorizationHeader(req: HttpRequest<any>, user: User): HttpRequest<any> {
		const body: any = Object.assign({}, req.body);
		body.authorization = user.jwt;

		const formData: any = $.param(body);

		return req.clone({
			setHeaders: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
			body: formData
		});
	}
}
